/**
 * PasskeySignInButton — Renders in AuthModal to offer Face ID / passkey re-auth.
 * Silently hides itself when the browser doesn't support WebAuthn.
 */
import { useState } from "react";
import { Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { isPasskeySupported, signInWithPasskey } from "@/lib/passkeys";
import { logger } from "@/utils/logger";

export function PasskeySignInButton() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  if (!isPasskeySupported()) return null;

  const handleClick = async () => {
    setBusy(true);
    try {
      await signInWithPasskey();
      // Session is set via supabase.auth; AuthModal listens to SIGNED_IN.
    } catch (err: any) {
      logger.error("Passkey sign-in failed", err);
      toast({
        title: "Passkey sign-in failed",
        description: err?.message || "Try email or Google instead.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={handleClick}
      disabled={busy}
      className="w-full flex items-center justify-center gap-2"
      aria-label="Sign in with a passkey or Face ID"
    >
      <Fingerprint className="h-5 w-5 text-primary" />
      {busy ? "Waiting for device…" : "Sign in with Face ID / passkey"}
    </Button>
  );
}
